import { ANCHOR_LABELS, ANCHORS, type Anchor } from '../lib/placement-presets';

/**
 * A glyph per cell, pointing the way the button moves the print: the four corners as filled
 * triangles, the four edges as arrows, the middle as a dot. Plain characters rather than icons or a
 * sprite sheet — nine buttons are not worth a dependency, and every one of these is in the system
 * fonts the app already asks for. `aria-hidden`, because the button's own `aria-label` is the name
 * ("Align top left"); the glyph is only what the eye reads.
 */
const GLYPHS: Record<Anchor, string> = {
  tl: '◤', t: '▲', tr: '◥',
  l: '◀', c: '●', r: '▶',
  bl: '◣', b: '▼', br: '◢',
};

/**
 * Nine 24-px buttons in a 3 × 3 grid: one press puts the print in that corner, against that edge or
 * dead centre of its area. The middle column and row write the sticky `'center'`
 * (`alignPlacement`), so pressing them ticks the panel's own Centre toggles.
 *
 * `value` is the anchor the placement ALREADY is (the caller computes it with `currentAnchor`, an
 * exact match), shown pressed. `label` names the group — "Align" for the primary print, and
 * "Align (Sleeve)" in a "More prints" row, so a screen reader entering one of several grids on a
 * multi-area blank is told which print it is about; the buttons themselves keep the same nine
 * names, which is what the grid's own label is for.
 */
export function AlignGrid({ value, label = 'Align', disabled, onPick }: {
  value?: Anchor | null;
  label?: string;
  disabled?: boolean;
  onPick: (anchor: Anchor) => void;
}) {
  return (
    <div className="align-grid" role="group" aria-label={label}>
      {ANCHORS.map((a) => (
        <button
          key={a} type="button" aria-label={ANCHOR_LABELS[a]} title={ANCHOR_LABELS[a]}
          aria-pressed={value === a} disabled={disabled} onClick={() => onPick(a)}
        >
          <span aria-hidden="true">{GLYPHS[a]}</span>
        </button>
      ))}
    </div>
  );
}
